import type { RemoteCursor } from '@/hooks/useRealtimeCollaboration';
import { MousePointer2 } from 'lucide-react';
import { useAppStore } from '@/stores/appStore';

interface RemoteCursorIndicatorProps {
  remoteCursors: Map<string, RemoteCursor>;
}

/**
 * Renders live cursor indicators for other users
 * Shows real-time cursor positions with user names
 */
export function RemoteCursorIndicator({ remoteCursors }: RemoteCursorIndicatorProps) {
  // Clean up stale cursor states (older than 3 seconds)
  const now = Date.now();
  const STALE_THRESHOLD_MS = 3000;

  // Get current viewport state to convert floor plan coordinates to screen coordinates
  const viewport = useAppStore(state => state.floorPlan.viewport);

  // Generate unique colors for each user based on their userId
  const getUserColor = (userId: string) => {
    // Simple hash function to generate consistent color per user
    let hash = 0;
    for (let i = 0; i < userId.length; i++) {
      hash = userId.charCodeAt(i) + ((hash << 5) - hash);
    }

    const hue = hash % 360;
    return `hsl(${hue}, 70%, 50%)`;
  };

  return (
    <>
      {Array.from(remoteCursors.values()).map(cursor => {
        // Skip stale cursors
        if (now - cursor.timestamp > STALE_THRESHOLD_MS) {
          return null;
        }

        const color = getUserColor(cursor.userId);

        // Convert floor plan coordinates to viewport screen coordinates
        // cursor.x and cursor.y are in floor plan space, we need to transform them
        const screenX = cursor.x * viewport.scale + viewport.positionX;
        const screenY = cursor.y * viewport.scale + viewport.positionY;

        return (
          <div
            key={cursor.userId}
            className="pointer-events-none absolute z-50 transition-all duration-75"
            style={{
              left: `${screenX}px`,
              top: `${screenY}px`,
            }}
          >
            {/* Cursor pointer icon */}
            <MousePointer2
              className="h-5 w-5"
              style={{ color }}
              strokeWidth={2}
              fill={color}
              fillOpacity={0.3}
            />

            {/* User name label */}
            <div
              className="ml-5 -mt-1 whitespace-nowrap rounded-md px-2 py-1 text-xs font-medium text-white shadow-lg"
              style={{ backgroundColor: color }}
            >
              {cursor.userName}
            </div>
          </div>
        );
      })}
    </>
  );
}
